import { useEffect } from "react";

interface SEOHeadProps {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl?: string;
  ogType?: string;
  ogImage?: string;
  schemaJson?: Record<string, unknown>[];
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalUrl = "https://www.aaark.com",
  ogType = "website",
  ogImage,
  schemaJson,
}: SEOHeadProps) {
  useEffect(() => {
    document.title = title;

    const setMeta = (name: string, content: string, propName = "name") => {
      let el = document.querySelector(`meta[${propName}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(propName, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta("description", description);
    setMeta("keywords", keywords);
    setMeta("og:title", title, "property");
    setMeta("og:description", description, "property");
    setMeta("og:type", ogType, "property");
    setMeta("og:url", canonicalUrl, "property");
    if (ogImage) setMeta("og:image", ogImage, "property");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);

    const existingLink = document.querySelector('link[rel="canonical"]');
    if (existingLink) existingLink.setAttribute("href", canonicalUrl);
    else {
      const link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      link.setAttribute("href", canonicalUrl);
      document.head.appendChild(link);
    }

    if (schemaJson && schemaJson.length > 0) {
      const existingScripts = document.querySelectorAll('script[data-seo-schema]');
      existingScripts.forEach((s) => s.remove());

      schemaJson.forEach((schema) => {
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.setAttribute("data-seo-schema", "true");
        script.textContent = JSON.stringify(schema);
        document.head.appendChild(script);
      });
    }

    return () => {
      const scripts = document.querySelectorAll('script[data-seo-schema]');
      scripts.forEach((s) => s.remove());
    };
  }, [title, description, keywords, canonicalUrl, ogType, ogImage, schemaJson]);

  return null;
}